/**
 * RearrangeSentenceEditor Component
 *
 * UI for editing REARRANGE_SENTENCE question type.
 * Allows admin to:
 * 1. Enter AI text as the correct sentence
 * 2. Drag and drop words to arrange the student-facing order
 */

import React, {useEffect, useState} from 'react';
import {Input, Button, Typography, Empty, Space, Divider} from 'antd';
import type {QuestionWithNewFields} from '../../Questions/QuestionTypes';

const {Text} = Typography;
const {TextArea} = Input;

export interface RearrangeSentenceEditorProps {
  question: QuestionWithNewFields;
  onAITextChange: (newText: string) => void;
  onShuffledOrderChange: (shuffledOrder: string[]) => void;
}

export const RearrangeSentenceEditor: React.FC<
  RearrangeSentenceEditorProps
> = ({question, onAITextChange, onShuffledOrderChange}) => {
  const [aiText, setAiText] = useState(question.textAI || '');
  const [words, setWords] = useState<string[]>([]);
  const [shuffledWords, setShuffledWords] = useState<string[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const extractWords = (text: string) =>
    text
      .trim()
      .split(/\s+/)
      .map(word => word.replace(/[.,!?;:\-\u2013\u2014]+$/g, ''))
      .filter(word => word.length > 0);

  useEffect(() => {
    const extractedWords = extractWords(aiText);

    setWords(extractedWords);
    setShuffledWords(extractedWords);
    onShuffledOrderChange(extractedWords);
    // Keep this dependent on aiText only; parent callbacks are recreated inline.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiText]);

  const handleAITextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setAiText(newText);
    onAITextChange(newText);
  };

  const handleShuffleWords = () => {
    const shuffled = [...shuffledWords].sort(() => Math.random() - 0.5);
    setShuffledWords(shuffled);
    onShuffledOrderChange(shuffled);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (dropIndex: number) => {
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newShuffledWords = [...shuffledWords];
    const [draggedWord] = newShuffledWords.splice(draggedIndex, 1);
    newShuffledWords.splice(dropIndex, 0, draggedWord);

    setShuffledWords(newShuffledWords);
    onShuffledOrderChange(newShuffledWords);
    setDraggedIndex(null);
  };

  const handleMoveUp = (index: number) => {
    if (index <= 0) return;

    const newOrder = [...shuffledWords];
    [newOrder[index - 1], newOrder[index]] = [
      newOrder[index],
      newOrder[index - 1],
    ];
    setShuffledWords(newOrder);
    onShuffledOrderChange(newOrder);
  };

  const handleMoveDown = (index: number) => {
    if (index >= shuffledWords.length - 1) return;

    const newOrder = [...shuffledWords];
    [newOrder[index], newOrder[index + 1]] = [
      newOrder[index + 1],
      newOrder[index],
    ];
    setShuffledWords(newOrder);
    onShuffledOrderChange(newOrder);
  };

  const handleResetOrder = () => {
    setShuffledWords(words);
    onShuffledOrderChange(words);
  };

  return (
    <div
      style={{
        marginTop: '20px',
        marginBottom: '20px',
        border: '2px solid #1890ff',
        padding: '15px',
        borderRadius: '4px',
        backgroundColor: '#f0f5ff',
      }}>
      <Text
        strong
        style={{fontSize: '16px', display: 'block', marginBottom: '10px'}}>
        REARRANGE_SENTENCE Editor
      </Text>

      <div style={{marginBottom: '20px'}}>
        <Text strong style={{display: 'block', marginBottom: '5px'}}>
          Correct Sentence (AI Text) *
        </Text>
        <TextArea
          value={aiText}
          onChange={handleAITextChange}
          placeholder="Enter the correct sentence (e.g., 'The quick brown fox')"
          rows={2}
          style={{marginBottom: '5px'}}
        />
        <Text type="secondary" style={{fontSize: '12px'}}>
          Words will be extracted and displayed below
        </Text>
      </div>

      <Divider style={{margin: '15px 0'}} />

      {words.length > 0 ? (
        <div>
          <Text strong style={{display: 'block', marginBottom: '10px'}}>
            Correct Order:{' '}
            {words.map((word, index) => (
              <span
                key={`${word}-${index}`}
                style={{
                  display: 'inline-block',
                  marginRight: '8px',
                  marginBottom: '6px',
                  backgroundColor: '#e6f7ff',
                  padding: '2px 6px',
                  borderRadius: '2px',
                }}>
                {word}
              </span>
            ))}
          </Text>

          <Divider style={{margin: '15px 0'}} />

          <div style={{marginBottom: '20px'}}>
            <Space>
              <Button type="primary" onClick={handleShuffleWords}>
                Random Shuffle
              </Button>
              <Button onClick={handleResetOrder}>Reset to Original</Button>
            </Space>
          </div>

          <Text strong style={{display: 'block', marginBottom: '10px'}}>
            Students Will See This Order (Drag to Rearrange):
          </Text>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-start',
              gap: '8px',
              padding: '15px',
              backgroundColor: 'white',
              border: '1px dashed #d9d9d9',
              borderRadius: '4px',
              minHeight: '60px',
            }}>
            {shuffledWords.length > 0 ? (
              shuffledWords.map((word, index) => (
                <div
                  key={`${word}-${index}`}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(index)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    maxWidth: '100%',
                    padding: '10px 12px',
                    backgroundColor:
                      draggedIndex === index ? '#ffc069' : '#1890ff',
                    color: 'white',
                    borderRadius: '4px',
                    cursor: 'move',
                    userSelect: 'none',
                    transition: 'all 0.2s',
                    opacity: draggedIndex === index ? 0.7 : 1,
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}>
                  {word}
                </div>
              ))
            ) : (
              <Empty
                description="Enter a sentence to create draggable words"
                style={{width: '100%', margin: '20px 0'}}
              />
            )}
          </div>

          {shuffledWords.length > 0 && (
            <div style={{marginTop: '15px'}}>
              <Text strong style={{display: 'block', marginBottom: '10px'}}>
                Or use arrow buttons to reorder:
              </Text>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px'}}>
                {shuffledWords.map((word, index) => (
                  <div
                    key={`${word}-${index}`}
                    style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                    <span
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#f0f0f0',
                        borderRadius: '2px',
                        whiteSpace: 'nowrap',
                      }}>
                      {word}
                    </span>
                    <Space size={0}>
                      <Button
                        size="small"
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0}>
                        Up
                      </Button>
                      <Button
                        size="small"
                        onClick={() => handleMoveDown(index)}
                        disabled={index === shuffledWords.length - 1}>
                        Down
                      </Button>
                    </Space>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div
            style={{
              marginTop: '15px',
              padding: '10px',
              backgroundColor: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: '4px',
            }}>
            <Text type="success" strong>
              Shuffled Order Saved: {shuffledWords.join(' ')}
            </Text>
          </div>
        </div>
      ) : (
        <Empty description="Enter a sentence above to start arranging words" />
      )}
    </div>
  );
};
